import { Camera, Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { PhotoThumbnail } from '../PhotoThumbnail';
import { CameraCaptureButton } from '../capture/CameraCaptureButton';
import type { PhotoSlot } from '../capture/photoSlotModel';

/**
 * Eine Kachel im Raster: offen = Kamera, fertig = das Foto als Grund mit „Neu aufnehmen“.
 * Neu aufnehmen löscht das Foto zuerst (wie bisher) und geht auch nach dem Abschluss: der Server
 * öffnet die Aufgabe dann wieder, und mit dem neuen Foto ist sie erneut erledigt.
 */
export function RoomTile({
  taskId,
  slot,
  busy,
  failed,
  captureLocked,
  retakeLocked,
  onCapture,
  onRetake,
}: {
  taskId: string;
  slot: PhotoSlot;
  busy: boolean;
  failed: boolean;
  /** Noch nicht gestartet, entfallen oder ohne Verbindung: nichts aufnehmen. */
  captureLocked: boolean;
  /** Ohne Recht oder ohne Verbindung: nichts löschen. */
  retakeLocked: boolean;
  onCapture: (file: File) => void;
  onRetake: (photoId: string) => void;
}) {
  const { item, photo } = slot;
  if (photo)
    return (
      <div className="rounded-tile bg-forest relative flex h-[118px] flex-col items-center justify-end overflow-hidden text-white">
        <PhotoThumbnail
          taskId={taskId}
          photo={photo}
          label={item.text}
          className="absolute inset-0 h-full w-full rounded-none border-0 opacity-45"
        />
        <span className="relative flex items-center gap-1 text-[13px] font-bold">
          <Check size={15} strokeWidth={2.5} aria-hidden /> {item.text}
        </span>
        {retakeLocked ? (
          <span className="relative pb-3 text-[11px] font-semibold">fertig</span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => onRetake(photo.id)}
            aria-label={`${item.text}: Foto löschen und neu aufnehmen`}
            className="relative min-h-11 px-3 text-[11px] font-semibold underline-offset-2 hover:underline disabled:opacity-60"
          >
            {busy ? 'wird gelöscht …' : 'fertig · neu aufnehmen'}
          </button>
        )}
      </div>
    );
  return (
    <div aria-busy={busy}>
      <CameraCaptureButton
        onCapture={onCapture}
        disabled={busy || captureLocked}
        className={cn(
          'rounded-tile text-forest flex h-[118px] w-full flex-col items-center justify-center gap-1.5 border-2 border-dashed bg-white',
          failed ? 'border-sealwax' : 'border-forest',
        )}
      >
        <Camera size={28} strokeWidth={1.8} aria-hidden />
        <span className="text-ink text-[13px] font-bold">{item.text}</span>
        <span className={cn('text-[11px]', failed && 'text-sealwax font-semibold')}>
          {busy
            ? 'Foto wird gespeichert …'
            : failed
              ? 'Nicht gespeichert – nochmal'
              : 'Foto aufnehmen'}
        </span>
      </CameraCaptureButton>
      <span role="alert" className="sr-only">
        {failed ? `${item.text}: Foto nicht gespeichert.` : ''}
      </span>
    </div>
  );
}
